import { cn } from "@/lib/utils";
import type { FileRecord } from "@cohesive/api-client";
import { formatDistanceToNow } from "date-fns";
import { Check, ExternalLink, Globe, Link2 } from "lucide-react";
import React from "react";
import { FileRecordPreview, type MediaIntent } from "../../FileRecordPreview";
import {
  BaseEntityCard,
  BaseEntityCardContent,
  type BaseEntityCardProps,
} from "../BaseCard";
import { FileRecordTypeIcon } from "./FileRecordTypeIcon";

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`;
}

export function getFileNameFromPath(path: string): string {
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

/**
 * Returns a compact human-readable label for a URL: `hostname/pathname`,
 * stripping `www.` and trailing slashes. Used as the visible link text on
 * cards whose file record originated from an external URL.
 */
export function urlLabel(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    const host = u.hostname.replace(/^www\./, "");
    const path = u.pathname.replace(/\/$/, "");
    return path ? `${host}${path}` : host;
  } catch {
    return rawUrl;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// FileRecordCard — single presentational variant (workspace, chat, canvas)
// ────────────────────────────────────────────────────────────────────────────

/** Optional LLM snapshot title when `path` is not yet populated (pre-hydration). */
type FileRecordCardExtras = {
  /** Snapshot from entity emission; prefer `path` leaf name when hydrated. */
  title?: string;
  /** Canvas / grid selection ring */
  isSelected?: boolean;
  /** Alias used when props are spread from callers that use `selected`. */
  selected?: boolean;
  /** When false, hides filename/footer row. Default true. */
  showFooter?: boolean;
  /**
   * When true, the card renders without its own chrome (border, shadow,
   * rounded corners) and fills its parent container's height. Used by
   * canvas spatial nodes where the React Flow node + selection ring is
   * the chrome. Independent of `showFooter` — an embedded card can still
   * render a compact filename strip below the thumb.
   */
  embedded?: boolean;
  /**
   * Layout variant:
   * - `"default"` — square thumbnail on top, footer below (workspace, chat image attachments, detail picker)
   * - `"compact"` — horizontal row: small square thumbnail left, title + meta right (chat file attachments)
   */
  variant?: "default" | "compact";
  /** e.g. workspace grid `WorkspaceFileRecordMenuSlot` (download submenus, …). */
  menuSlot?: React.ReactNode;
  /**
   * Marks this card as a REFERENCE appearance (a live link to a Library
   * file). Derived from `kind === "link"` when omitted; container surfaces
   * whose entity carries the target's id pass it explicitly.
   */
  isReference?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  /**
   * `thumbnail` — grid cards use CloudFront `preview_urls["640"]` when present.
   * `full` — canvas tiles / large previews use the main object `url`.
   */
  mediaIntent?: MediaIntent;
  /** Canvas video upload handoff — revoke local blob after server video paints. */
  onVideoDisplayReady?: () => void;
  /**
   * Optional render-prop wrapping just the thumbnail well. Surfaces
   * like the workspace detail page pass a hover-dwell-preview wrapper
   * here so a slow hover over the image surfaces a larger preview,
   * while hovering the title/timestamp footer is left alone.
   */
  thumbnailWrapper?: (thumb: React.ReactNode) => React.ReactNode;
};

export type FileRecordCardProps = FileRecord &
  Omit<BaseEntityCardProps, "onClick" | "children"> &
  FileRecordCardExtras;

/**
 * Workspace file cards used a per-file aspect ratio derived from
 * `width` / `height`, which made tall portrait
 * images (e.g. iPhone screenshots) render as ~470px-tall tiles inside
 * the 220px-wide grid and tower over every other card. Cards should
 * always be a fixed size regardless of image dimensions, so we lock
 * the thumbnail well to a 2:1 ratio and let `object-cover` (with
 * `object-top` for derived previews, set in `FileRecordThumbnail`)
 * crop oversized originals. Canvas spatial nodes use `embedded` mode
 * which fills the React Flow node box instead.
 */
const THUMBNAIL_ASPECT_CLASS = "aspect-[2/1]";

/**
 * One card for every surface. Parents hydrate `entity.data` to a `FileRecord`
 * shape (or merge fetch result) before `EntityCard` dispatches here.
 */
export function FileRecordCard({
  id,
  path,
  content_type,
  url,
  preview_urls,
  size,
  updated_at,
  status,
  origin_type,
  origin_url,
  kind,
  published,
  isReference: isReferenceProp,
  title: snapshotTitle,
  isSelected = false,
  selected = false,
  showFooter = true,
  embedded = false,
  variant = "default",
  mediaIntent = "thumbnail",
  menuSlot,
  processes,
  actions,
  platform: _platform,
  toolbarMode,
  chatOpen,
  onChatOpenChange,
  onQuickChatSubmit,
  onDeleteClick: _onDeleteClick,
  onDetailClick: _onDetailClick,
  onMentionClick: _onMentionClick,
  onClick,
  onDoubleClick,
  className,
  style,
  thumbnailWrapper,
  onVideoDisplayReady,
}: FileRecordCardProps) {
  // Both `isSelected` and `selected` are accepted for legacy reasons —
  // some call sites pass one, some the other (e.g. EntityCard forwards
  // `selected`). Either flips the selection ring.
  const showSelected = isSelected || selected;
  const displayTitle =
    (path && getFileNameFromPath(path)) || snapshotTitle || id || "File";

  const statusStr = status as string | undefined;

  const editedTime = updated_at
    ? formatDistanceToNow(new Date(updated_at), {
        addSuffix: true,
      }).replace(/^about /, "")
    : "";

  const selectionClassName =
    showSelected &&
    !embedded &&
    "border-rust/60 ring-2 ring-rust/60 ring-offset-2 ring-offset-background";

  const compactThumbnail = (
    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg relative">
      <FileRecordPreview
        contentType={content_type}
        originType={origin_type}
        url={url}
        previewUrls={preview_urls}
        status={statusStr}
        alt={displayTitle}
        mediaIntent={mediaIntent}
        embedded={embedded}
        spinnerSize="sm"
        className="h-full w-full"
        iconClassName="h-5 w-5"
        onVideoDisplayReady={onVideoDisplayReady}
      />
    </div>
  );

  // Provenance — a compact cluster pinned to the RIGHT of the existing
  // footer lines (never its own row): it rides the meta line when one
  // renders, else the title line. Cards without a footer (image/video
  // tiles) show nothing — the artwork stays clean.
  const isReference = isReferenceProp ?? kind === "link";
  const provenanceBadges = (isReference || published) && (
    <span
      className={cn(
        "ml-auto flex shrink-0 items-center gap-1.5 pl-2 leading-none",
        embedded ? "text-[10px]" : "text-[11px]",
      )}
    >
      {isReference && (
        <span
          className="inline-flex items-center gap-1 font-medium text-gold"
          title="From the Library — a live link. Removing it here never touches the original."
          data-testid="file-card-reference-badge"
        >
          <Link2 className="h-3 w-3 shrink-0" />
          From Library
        </span>
      )}
      {published && (
        <span
          className="inline-flex items-center gap-1 font-medium text-lc-active"
          title="Published — this file has a public link."
          data-testid="file-card-published-badge"
        >
          <Globe className="h-3 w-3 shrink-0" />
          Published
        </span>
      )}
    </span>
  );

  const hasMeta = Boolean(editedTime || (size && size > 0));

  const titleRow = (
    <div className="flex items-center gap-1.5 min-w-0">
      <FileRecordTypeIcon
        contentType={content_type || ""}
        originType={origin_type}
        className="h-3.5 w-3.5 shrink-0"
      />
      {origin_url ? (
        <a
          href={origin_url}
          target="_blank"
          rel="noopener noreferrer"
          title={origin_url}
          onClick={(e) => e.stopPropagation()}
          className="group/title flex items-center gap-1 min-w-0"
        >
          <span
            className={cn(
              "font-semibold text-foreground leading-tight truncate group-hover/title:underline underline-offset-2",
              embedded ? "text-[12px]" : "text-[13px]",
            )}
          >
            {urlLabel(origin_url)}
          </span>
          <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover/title:opacity-100 transition-opacity" />
        </a>
      ) : (
        <p
          className={cn(
            "font-semibold text-foreground leading-tight truncate",
            embedded ? "text-[12px]" : "text-[13px]",
          )}
        >
          {displayTitle}
        </p>
      )}
      {!hasMeta && provenanceBadges}
    </div>
  );

  const metaRow = hasMeta && (
    <p
      className={cn(
        "flex items-center text-muted-foreground leading-none",
        embedded ? "text-[10px] mt-1" : "text-[11px] mt-0.5",
        variant === "default" && !embedded && "mt-1.5",
      )}
    >
      <span className="min-w-0 truncate">
        {editedTime}
        {editedTime && size != null && size > 0 && " · "}
        {size != null && size > 0 && formatFileSize(size)}
      </span>
      {provenanceBadges}
    </p>
  );

  if (variant === "compact") {
    return (
      <BaseEntityCard
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        processes={processes}
        actions={actions}
        toolbarMode={toolbarMode}
        chatOpen={chatOpen}
        onChatOpenChange={onChatOpenChange}
        onQuickChatSubmit={onQuickChatSubmit}
        className={cn(
          "group overflow-hidden rounded-xl border bg-card shadow-sm hover:shadow-md",
          selectionClassName,
          className,
        )}
        style={style}
      >
        {compactThumbnail}

        <BaseEntityCardContent>
          {titleRow}
          {metaRow}
        </BaseEntityCardContent>

        {menuSlot && <div className="shrink-0">{menuSlot}</div>}
      </BaseEntityCard>
    );
  }

  // ── Layout primitives ──────────────────────────────────────────────────────
  // `embedded` = "I'm inside a host container (e.g. React Flow node) that
  // handles its own chrome — drop ours and fill the host's box."
  // `showFooter` = "Render the filename strip below the thumb."
  // The two are independent so canvas non-image/video tiles can render a
  // compact footer label without picking up the workspace card chrome.

  const footerEl = showFooter && (
    <div
      className={cn(
        "flex items-center justify-between gap-2 shrink-0",
        embedded ? "px-2.5 py-2" : "px-3 py-3.5",
      )}
    >
      <div className="flex-1 min-w-0">
        {titleRow}
        {metaRow}
      </div>

      {menuSlot && <div className="shrink-0">{menuSlot}</div>}
    </div>
  );

  // Layout primitives:
  //   `embedded` — host (e.g. React Flow node) owns the chrome; we drop
  //                 the card border/shadow/rounding and fill its box.
  //   `showFooter` — render the filename strip below the thumbnail.
  // The two are independent so canvas non-image/video tiles can render
  // a compact footer without picking up workspace card chrome.
  const thumbWell = (
    <div
      className={cn(
        "relative w-full overflow-hidden group",
        embedded
          ? showFooter
            ? "flex-1 min-h-0"
            : "min-h-0 h-full aspect-square"
          : showFooter
            ? THUMBNAIL_ASPECT_CLASS
            : "min-h-0 h-full aspect-square",
      )}
    >
      <FileRecordPreview
        contentType={content_type}
        originType={origin_type}
        url={url}
        previewUrls={preview_urls}
        status={statusStr}
        alt={displayTitle}
        mediaIntent={mediaIntent}
        embedded={embedded}
        spinnerSize="lg"
        spinnerLabel
        className={cn(
          showFooter ? "w-full h-full" : "absolute inset-0 w-full h-full",
        )}
        onVideoDisplayReady={onVideoDisplayReady}
      />
    </div>
  );

  return (
    <BaseEntityCard
      layout="stack"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      actions={actions}
      toolbarMode={toolbarMode}
      chatOpen={chatOpen}
      onChatOpenChange={onChatOpenChange}
      onQuickChatSubmit={onQuickChatSubmit}
      className={cn(
        "group relative overflow-hidden",
        embedded
          ? "w-full h-full min-h-0 rounded-xl border-0 shadow-none hover:shadow-2xl flex flex-col bg-card [&>div]:flex [&>div]:flex-1 [&>div]:min-h-0 [&>div]:flex-col"
          : "rounded-2xl border shadow-md hover:shadow-xl",
        selectionClassName,
        className,
      )}
      style={{ containerType: "inline-size", ...style }}
    >
      {thumbnailWrapper ? thumbnailWrapper(thumbWell) : thumbWell}

      {showSelected && !embedded && showFooter && (
        <div className="absolute top-2 right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-rust text-white ring-1 ring-black/40 dark:ring-black/70">
          <Check className="h-3 w-3" />
        </div>
      )}

      {footerEl}
    </BaseEntityCard>
  );
}
